import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { api, type DeliveryConfig } from "@/api";
import { settingsKeys, useDeliveryConfig } from "@/api/hooks/use-settings";
import { Button } from "@osmix/ui/button";
import { Input } from "@osmix/ui/input";
import { Label } from "@osmix/ui/label";
import { formatDateTime } from "@/lib/utils";
import { DeliveryCarriers } from "@/components/delivery-carriers";
type Draft = {
  standard: string;
  express: string;
  pickup: string;
  freeOver: string;
};
const toDraft = (c: DeliveryConfig): Draft => ({
  standard: String(c.fees.standard),
  express: String(c.fees.express),
  pickup: String(c.fees.pickup),
  freeOver: String(c.freeOver)
});

// Delivery pricing editor — the single source of truth the storefront reads
// at checkout. Saving PATCHes the gateway's settings; the change is live for
// the next order immediately.
export function DeliverySettingsPage() {
  const {
    t
  } = useTranslation();
  const queryClient = useQueryClient();
  const {
    data,
    error: loadError
  } = useDeliveryConfig();
  const config = data ?? null;
  const [draft, setDraft] = useState<Draft | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  // Seed the editable draft once, from the first fetched config; background
  // refetches must not clobber in-progress edits.
  useEffect(() => {
    if (config) setDraft(d => d ?? toDraft(config));
  }, [config]);
  const error = actionError ?? (loadError ? loadError.message : null);
  if (error) return <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
				{error}
			</div>;
  if (!draft || !config) return <p className="text-muted-foreground">{t("delivery.loading")}</p>;
  const num = (s: string) => {
    const n = Number(s);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  };
  const save = async () => {
    setSaving(true);
    setActionError(null);
    try {
      const updated = await api.settings.updateDelivery({
        fees: {
          standard: num(draft.standard),
          express: num(draft.express),
          pickup: num(draft.pickup)
        },
        freeOver: num(draft.freeOver)
      });
      queryClient.setQueryData(settingsKeys.delivery, updated);
      setDraft(toDraft(updated));
      setSavedAt(updated.updatedAt);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };
  const set = (key: keyof Draft) => (e_0: React.ChangeEvent<HTMLInputElement>) => setDraft(d_0 => d_0 ? {
    ...d_0,
    [key]: e_0.target.value
  } : d_0);
  const dirty = JSON.stringify(draft) !== JSON.stringify(toDraft(config));
  const fees: {
    key: keyof Draft;
    label: string;
  }[] = [{
    key: "standard",
    label: t("delivery.methods.standard")
  }, {
    key: "express",
    label: t("delivery.methods.express")
  }, {
    key: "pickup",
    label: t("delivery.methods.pickup")
  }];
  return <div className="flex flex-col gap-10">
			<div className="flex max-w-xl flex-col gap-6">
				<div className="flex flex-col gap-1" data-tour="delivery-intro">
					<h1 className="text-2xl font-bold">{t("nav.delivery")}</h1>
					<p className="text-sm text-muted-foreground">{t("delivery.intro")}</p>
					<p className="text-sm text-muted-foreground">{t("delivery.fallbackNote")}</p>
				</div>

				<section className="flex flex-col gap-4 rounded-lg border p-5" data-tour="delivery-fees">
					<h2 className="text-sm font-semibold uppercase text-muted-foreground">
						{t("delivery.feesHeading")}
					</h2>
					<div className="grid gap-4 sm:grid-cols-3">
						{fees.map(({
            key: key_0,
            label
          }) => <div key={key_0} className="flex flex-col gap-1.5">
								<Label htmlFor={`fee-${key_0}`}>{label}</Label>
								<Input id={`fee-${key_0}`} type="number" min={0} value={draft[key_0]} onChange={set(key_0)} />
							</div>)}
					</div>
				</section>

				<section className="flex flex-col gap-2 rounded-lg border p-5" data-tour="delivery-free">
					<Label htmlFor="freeOver">{t("delivery.freeOver")}</Label>
					<Input id="freeOver" type="number" min={0} value={draft.freeOver} onChange={set("freeOver")} />
					<p className="text-xs text-muted-foreground">{t("delivery.freeOverHint")}</p>
				</section>

				<div className="flex items-center gap-3" data-tour="delivery-save">
					<Button onClick={save} disabled={saving || !dirty}>
						{saving ? t("delivery.saving") : t("delivery.save")}
					</Button>
					{savedAt && !dirty && <span className="text-sm text-muted-foreground">
							{t("delivery.savedAt", {
            time: formatDateTime(savedAt)
          })}
						</span>}
				</div>
			</div>
			<DeliveryCarriers />
		</div>;
}